import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { driversApi } from '../../api/drivers';
import toast from 'react-hot-toast';

const driverSchema = z.object({
  nombre: z.string().min(1, 'El nombre es requerido'),
  apellido_paterno: z.string().min(1, 'El apellido paterno es requerido'),
  apellido_materno: z.string().min(1, 'El apellido materno es requerido'),
  telefono: z.string().min(10, 'El teléfono debe tener al menos 10 dígitos'),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  licencia: z.string().min(1, 'La licencia es requerida'),
  vencimiento_licencia: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido (YYYY-MM-DD)').optional().or(z.literal('')),
  tipo_vehiculo_asignado: z.string().optional(),
  documentacion_cargada: z.any().optional(), // File input
  activo: z.boolean(), // Checkbox returns boolean, we convert to string in onSubmit
});

const DriverForm = ({ driver, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const queryClient = useQueryClient();

  // Helper to convert activo to string
  const activoToString = (activo) => {
    if (activo === 1 || activo === true || activo === 'true') return 'true';
    if (activo === 0 || activo === false || activo === 'false') return 'false';
    return 'true';
  };

  // Handle file selection with validation
  const handleFileSelect = (file) => {
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no puede exceder 10MB');
      return;
    }

    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Solo se permiten archivos PDF, JPG, JPEG o PNG');
      return;
    }

    setDocumentFile(file);
    toast.success('Archivo seleccionado correctamente');
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(driverSchema),
    defaultValues: {
      nombre: driver?.nombre || '',
      apellido_paterno: driver?.apellido_paterno || '',
      apellido_materno: driver?.apellido_materno || '',
      telefono: driver?.telefono || '',
      email: driver?.email || '',
      licencia: driver?.licencia || '',
      vencimiento_licencia: driver?.vencimiento_licencia || '',
      tipo_vehiculo_asignado: driver?.tipo_vehiculo_asignado || '',
      activo: activoToString(driver?.activo) === 'true',
    },
  });

  const createMutation = useMutation({
    mutationFn: driversApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      toast.success('Conductor creado exitosamente');
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Error al crear conductor');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => driversApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      toast.success('Conductor actualizado exitosamente');
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Error al actualizar conductor');
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Convert activo from boolean to string "true" or "false"
      const submitData = {
        ...data,
        activo: data.activo ? 'true' : 'false'
      };

      // If there's a file, use FormData, otherwise send as JSON
      if (documentFile) {
        const formData = new FormData();
        Object.keys(submitData).forEach(key => {
          if (key !== 'documentacion_cargada') {
            formData.append(key, submitData[key]);
          }
        });
        formData.append('documentacion_cargada', documentFile);

        if (driver) {
          await updateMutation.mutateAsync({ id: driver.id, data: formData });
        } else {
          await createMutation.mutateAsync(formData);
        }
      } else {
        // No file, send as regular JSON object
        // Only include documentacion_cargada if it exists (for updates)
        if (driver?.documentacion_cargada) {
          submitData.documentacion_cargada = driver.documentacion_cargada;
        } else {
          delete submitData.documentacion_cargada;
        }

        if (driver) {
          await updateMutation.mutateAsync({ id: driver.id, data: submitData });
        } else {
          await createMutation.mutateAsync(submitData);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{driver ? 'Editar Conductor' : 'Nuevo Conductor'}</h2>
          <button className="modal-close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="nombre">Nombre *</label>
              <input
                id="nombre"
                type="text"
                {...register('nombre')}
                className={errors.nombre ? 'error' : ''}
              />
              {errors.nombre && (
                <span className="error-message">{errors.nombre.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="apellido_paterno">Apellido Paterno *</label>
              <input
                id="apellido_paterno"
                type="text"
                {...register('apellido_paterno')}
                className={errors.apellido_paterno ? 'error' : ''}
              />
              {errors.apellido_paterno && (
                <span className="error-message">{errors.apellido_paterno.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="apellido_materno">Apellido Materno *</label>
              <input
                id="apellido_materno"
                type="text"
                {...register('apellido_materno')}
                className={errors.apellido_materno ? 'error' : ''}
              />
              {errors.apellido_materno && (
                <span className="error-message">{errors.apellido_materno.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="telefono">Teléfono *</label>
              <input
                id="telefono"
                type="tel"
                {...register('telefono')}
                className={errors.telefono ? 'error' : ''}
              />
              {errors.telefono && (
                <span className="error-message">{errors.telefono.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                {...register('email')}
                className={errors.email ? 'error' : ''}
              />
              {errors.email && (
                <span className="error-message">{errors.email.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="licencia">Licencia *</label>
              <input
                id="licencia"
                type="text"
                {...register('licencia')}
                className={errors.licencia ? 'error' : ''}
              />
              {errors.licencia && (
                <span className="error-message">{errors.licencia.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="vencimiento_licencia">Vencimiento de Licencia</label>
              <input
                id="vencimiento_licencia"
                type="date"
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                {...register('vencimiento_licencia')}
                className={errors.vencimiento_licencia ? 'error' : ''}
              />
              {errors.vencimiento_licencia && (
                <span className="error-message">{errors.vencimiento_licencia.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="tipo_vehiculo_asignado">Tipo de Vehículo Asignado</label>
              <select
                id="tipo_vehiculo_asignado"
                {...register('tipo_vehiculo_asignado')}
                className={errors.tipo_vehiculo_asignado ? 'error' : ''}
              >
                <option value="">Selecciona un tipo</option>
                <option value="Sedán">Sedán</option>
                <option value="SUV">SUV</option>
                <option value="Van">Van</option>
                <option value="Pickup">Pickup</option>
                <option value="Camioneta">Camioneta</option>
              </select>
              {errors.tipo_vehiculo_asignado && (
                <span className="error-message">{errors.tipo_vehiculo_asignado.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="documentacion_cargada">Documentación</label>

              {/* File Upload Area */}
              <div
                style={{
                  border: `2px dashed ${isDragging ? 'var(--secondary)' : 'rgba(255,255,255,.2)'}`,
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'center',
                  background: isDragging ? 'rgba(245, 158, 11, 0.1)' : 'rgba(255,255,255,.03)',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  position: 'relative',
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    handleFileSelect(file);
                  }
                }}
                onClick={() => document.getElementById('documentacion_cargada')?.click()}
              >
                <input
                  id="documentacion_cargada"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleFileSelect(file);
                    }
                  }}
                  style={{ display: 'none' }}
                  className={errors.documentacion_cargada ? 'error' : ''}
                />

                {documentFile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '12px',
                      background: 'var(--secondary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '32px'
                    }}>
                      {documentFile.type?.includes('pdf') ? '📄' : '🖼️'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontWeight: '600', fontSize: '14px', color: '#eaeaea' }}>
                        {documentFile.name}
                      </span>
                      <span style={{ fontSize: '12px', opacity: 0.7 }}>
                        {(documentFile.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDocumentFile(null);
                        const input = document.getElementById('documentacion_cargada');
                        if (input) input.value = '';
                      }}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '6px',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>delete</span>
                      Eliminar
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '12px',
                      background: 'rgba(255,255,255,.05)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '32px'
                    }}>
                      📎
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontWeight: '600', fontSize: '14px', color: '#eaeaea' }}>
                        {isDragging ? 'Suelta el archivo aquí' : 'Arrastra un archivo o haz clic para seleccionar'}
                      </span>
                      <span style={{ fontSize: '12px', opacity: 0.7 }}>
                        PDF, JPG, PNG (máximo 10MB)
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {driver?.documentacion_cargada && !documentFile && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px'
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#3b82f6' }}>description</span>
                  <a
                    href={`${import.meta.env.VITE_API_URL?.replace('/api/app/v1', '') || 'https://api.racingmotorsmexico.com'}/${driver.documentacion_cargada}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#3b82f6',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>open_in_new</span>
                    Click para ver el documento actual
                  </a>
                </div>
              )}

              {errors.documentacion_cargada && (
                <span className="error-message" style={{ marginTop: '8px', display: 'block' }}>
                  {errors.documentacion_cargada.message}
                </span>
              )}
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '12px', marginBottom: '12px' }}>
              <label className="checkbox-label" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  {...register('activo')}
                />
                <span className="checkmark"></span>
                Activo
              </label>
              {errors.activo && (
                <span className="error-message" style={{ marginTop: '4px', display: 'block', width: '100%', textAlign: 'center' }}>
                  {errors.activo.message}
                </span>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Guardando...' : (driver ? 'Actualizar' : 'Crear')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DriverForm;
